import { Article } from '../articlesData';

export const articleKonsulentaftaleFreelancekontrakt: Article = {
  id: 'konsulentaftale-freelancekontrakt-skabelon',
  slug: 'konsulentaftale-freelancekontrakt-skabelon',
  title: 'Konsulentaftale: Beskyt dine rettigheder og honorarer med en skarp B2B-aftale',
  subtitle: 'Den ultimative guide til konsulent- og freelanceaftaler. Lær at afgrænse Scope of Work, sikre dine immaterielle rettigheder (IPR) og undgå skattemæssige faldgruber.',
  metaTitle: 'Konsulentaftale Skabelon med MitID: B2B Kontrakt | KvikSign',
  metaDescription: 'Gratis skabelon til konsulentaftale og freelancekontrakt. Få styr på honorar, IPR-rettigheder og ansvar, og luk aftalen digitalt med MitID og BankID.',
  canonicalUrl: 'https://kviksign.com/da/articles/konsulentaftale-freelancekontrakt-skabelon',
  heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FCasual%20work%20environment.webp?alt=media&token=f32203e2-8787-45ed-a53a-547f30452c8e',
  heroImageAlt: 'Konsulentaftale og B2B kontrakt underskrives med digital signatur',
  category: 'Kontrakter & B2B',
  author: {
    name: 'KvikSign',
    role: 'eID & Sikkerhedsteam',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-10-14',
  updatedAt: '2026-10-14',
  readingTime: '6 min læsetid',
  keywords: [
    'konsulentaftale skabelon',
    'freelancekontrakt digital signatur',
    'b2b samarbejdsaftale mitid',
    'scope of work konsulent',
    'ipr rettigheder konsulentaftale',
    'skat konsulent vs lønmodtager',
    'konsulentaftale timepris vilkår',
    'underleverandøraftale skabelon'
  ],
  keyTakeaways: [
    'En klar konsulentaftale er det vigtigste værn mod "scope creep" – opgaver der vokser i omfang, uden at honoraret følger med.',
    'Immaterielle rettigheder (ophavsret til kode, designs, rapporter) forbliver som udgangspunkt hos konsulenten, medmindre overdragelsen udtrykkeligt er aftalt på skrift.',
    'Skattestyrelsen vurderer nøje, om en konsulent reelt er en selvstændig erhvervsdrivende eller en skjult lønmodtager med risiko for efteropkrævning af A-skat og AM-bidrag.',
    'Ansvarsbegrænsningsklausuler sikrer, at konsulenten ikke risikerer personlig konkurs ved utilsigtede indirekte tab eller systemnedbrud hos kunden.',
    'Med KvikSign kan konsulenten få kunden til at godkende aftalen og opstartsbilagene med MitID eller BankID, før det første fakturerbare minut påbegyndes.'
  ],
  tableOfContents: [
    { id: 'hvorfor-enhver-opgave-kraever-en-skriftlig-aftale', title: 'Hvorfor enhver opgave kræver en skriftlig aftale' },
    { id: 'de-vigtigste-elementer-i-en-konsulentaftale', title: 'De 6 vigtigste elementer i en konsulentaftale' },
    { id: 'ipr-hvem-ejer-koden-designet-og-resultatet', title: 'IPR: Hvem ejer koden, designet og resultatet?' },
    { id: 'skattestyrelsens-kriterier-selvstaendig-eller-loenmodtager', title: 'Skattestyrelsens kriterier: Selvstændig eller lønmodtager?' },
    { id: 'ansvarsbegraensning-og-erstatning', title: 'Ansvarsbegrænsning og erstatning' },
    { id: 'download-gratis-konsulentaftale-skabelon', title: 'Download gratis konsulentaftale-skabelon' },
    { id: 'faq-konsulentaftale', title: 'Ofte stillede spørgsmål' }
  ],
  sections: [
    {
      id: 'hvorfor-enhver-opgave-kraever-en-skriftlig-aftale',
      title: 'Hvorfor enhver opgave kræver en skriftlig aftale',
      paragraphs: [
        'Uanset om du arbejder med IT-udvikling, management consulting, grafisk design eller markedsføring, opstår uenigheder sjældent af ond vilje, men af forventningskløfter.',
        'Kunden forventede måske ubegrænsede rettelsesrunder, døgnsupport eller ejerskab over alle råfiler, mens konsulenten forventede at fakturere efter medgået tid.',
        'En professionel konsulentaftale eliminerer disse misforståelser fra dag ét og sikrer, at begge parter ved præcis, hvad der leveres, og hvornår regningen forfalder.'
      ]
    },
    {
      id: 'de-vigtigste-elementer-i-en-konsulentaftale',
      title: 'De 6 vigtigste elementer i en konsulentaftale',
      paragraphs: [
        '• Ydelsesbeskrivelse (Scope of Work): Detaljeret specifikation af leverancen og eventuelle milepæle.',
        '• Honorar og betalingsbetingelser: Timepris, fastpris, udgiftsgodtgørelse og betalingsfrist (f.eks. netto 14 dage).',
        '• Ekstraarbejde: Hvad koster tilretninger ud over det aftalte scope?',
        '• Samarbejde og kundens medvirken: Hvilke adgange, data eller godkendelser skal kunden levere til tiden?',
        '• Opsigelse og ophør: Hvilket varsel gælder, hvis en af parterne ønsker at afslutte samarbejdet?',
        '• Lovvalg og værneting: Hvilke regler gælder ved eventuelle konflikter.'
      ]
    },
    {
      id: 'ipr-hvem-ejer-koden-designet-og-resultatet',
      title: 'IPR: Hvem ejer koden, designet og resultatet?',
      paragraphs: [
        'Ifølge ophavsretsloven tilhører ophavsretten altid den fysiske person, der har skabt værket. Kunden får som udgangspunkt kun en brugsret til det færdige resultat inden for aftalens formål.',
        'Hvis kunden skal have fuld ejendomsret til kildekoden, varemærkerne eller materialet, skal dette fremgå eksplicit af kontrakten. Mange konsulenter forbeholder sig retten til at genbruge generiske biblioteker, skabeloner og know-how (Background IPR).'
      ]
    },
    {
      id: 'skattestyrelsens-kriterier-selvstaendig-eller-loenmodtager',
      title: 'Skattestyrelsens kriterier: Selvstændig eller lønmodtager?',
      paragraphs: [
        'Hvis en konsulent arbejder fuld tid for kun én kunde over længere tid, følger kundens instruktioner og benytter kundens udstyr på kundens kontor, risikerer forholdet at blive omklassificeret til et ansættelsesforhold.',
        'Bliver man anset for lønmodtager, kan kunden hæfte for manglende indeholdelse af A-skat. En skarp konsulentaftale, der understreger konsulentens instruktionsfrihed, ret til at antage substitutter og egen økonomiske risiko, er et vigtigt værn mod dette.'
      ]
    },
    {
      id: 'ansvarsbegraensning-og-erstatning',
      title: 'Ansvarsbegrænsning og erstatning',
      paragraphs: [
        'Uden en ansvarsbegrænsning hæfter konsulenten principielt ubegrænset efter dansk rets almindelige erstatningsregler for det tab, som en fejl måtte forårsage for kunden.',
        'En standardklausul bør altid udelukke indirekte tab (f.eks. driftstab, tabt avance og tab af data) samt begrænse det samlede erstatningsansvar til det honorar, konsulenten har modtaget under aftalen (eller et fastsat loft).'
      ]
    },
    {
      id: 'download-gratis-konsulentaftale-skabelon',
      title: 'Download gratis konsulentaftale-skabelon',
      paragraphs: [
        'Hent vores fleksible skabelon til konsulent- og freelanceaftaler, der dækker timebaserede opgaver, projektleverancer, IPR-overdragelse og ansvarsbegrænsning.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Hvad sker der, hvis kunden ikke betaler til tiden?',
      answer: 'Hvis betalingsfristen overskrides, har konsulenten ret til at standse arbejdet og opkræve morarenter efter renteloven samt rykkergebyrer.'
    },
    {
      question: 'Kan konsulentaftalen underskrives af parter i Sverige eller Norge?',
      answer: 'Ja, KvikSign understøtter både dansk MitID, svensk BankID og eIDAS, så nordiske samarbejdsaftaler kan underskrives på få sekunder.'
    },
    {
      question: 'Er det nødvendigt med en NDA ud over konsulentaftalen?',
      answer: 'Mange konsulentaftaler indeholder en indbygget fortrolighedsklausul. Hvis I udveksler fortrolig viden forud for kontraktindgåelsen, bør en særskilt NDA dog underskrives først.'
    }
  ],
  sources: [
    { title: 'Ophavsretsloven (Retsinformation)', url: 'https://www.retsinformation.dk' },
    { title: 'Skattestyrelsen - Cirkulære om vurdering af selvstændig erhvervsdrivende', url: 'https://skat.dk' }
  ],
  disclaimer: 'Artiklen og skabelonen er af generel vejledende karakter og kan ikke erstatte konkret advokatbistand.'
};
